// All-products grid with category filtering. Previously a flat 28-card grid with no way to
// narrow it down, and a fixed templateRows that only made sense for the original 3 rows.
import { Box, Flex, Grid, Text, useColorModeValue } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ProductCard from "components/ProductCard/ProductCard";
import { products, allProductsOrder, categories, getImage } from "data/products";

const ALL = "all";

const FilterChip = ({ active, count, onClick, children }) => {
  const idleColor = useColorModeValue("#6B7A90", "gray.400");
  const idleBorder = useColorModeValue("#E1E7F0", "whiteAlpha.300");
  const idleHoverBorder = useColorModeValue("#B9CCE4", "whiteAlpha.400");
  const activeBg = useColorModeValue("brand.500", "brand.400");
  const countColor = active ? "whiteAlpha.700" : idleColor;

  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      align="center"
      gap="7px"
      flex="none"
      px="14px"
      py="8px"
      borderRadius="3px"
      border="1px solid"
      borderColor={active ? "transparent" : idleBorder}
      bg={active ? activeBg : "transparent"}
      color={active ? "white" : idleColor}
      fontFamily="mono"
      fontSize="11px"
      letterSpacing="0.1em"
      textTransform="uppercase"
      fontWeight={active ? "700" : "400"}
      transition="border-color 0.15s ease, background 0.15s ease, color 0.15s ease"
      _hover={active ? {} : { borderColor: idleHoverBorder }}
      _focusVisible={{ outline: "2px solid", outlineColor: "brand.400", outlineOffset: "2px" }}
      aria-pressed={active}
    >
      {children}
      <Text as="span" color={countColor} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {count}
      </Text>
    </Flex>
  );
};

const AllProducts = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(ALL);
  const mutedColor = useColorModeValue("#6B7A90", "gray.400");
  const borderColor = useColorModeValue("#E1E7F0", "whiteAlpha.200");

  // allProductsOrder is the curated display order; fall back to data order for any product
  // missing from it so nothing silently disappears from the catalogue.
  const ordered = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p]));
    const listed = allProductsOrder.map((id) => byId.get(id)).filter(Boolean);
    const listedIds = new Set(listed.map((p) => p.id));
    return [...listed, ...products.filter((p) => !listedIds.has(p.id))];
  }, []);

  const counts = useMemo(() => {
    const map = { [ALL]: ordered.length };
    categories.forEach((c) => {
      map[c.key] = ordered.filter((p) => p.category === c.key).length;
    });
    return map;
  }, [ordered]);

  const visible =
    activeCategory === ALL
      ? ordered
      : ordered.filter((p) => p.category === activeCategory);

  return (
    <Card p={{ base: "20px", md: "26px" }} my="24px">
      <Flex
        as="nav"
        aria-label={t("common:allProducts.filterLabel")}
        gap="8px"
        flexWrap="wrap"
        pb="22px"
        mb="22px"
        borderBottom="1px solid"
        borderColor={borderColor}
      >
        <FilterChip
          active={activeCategory === ALL}
          count={counts[ALL]}
          onClick={() => setActiveCategory(ALL)}
        >
          {t("common:allProducts.all")}
        </FilterChip>
        {categories.map((c) => (
          <FilterChip
            key={c.key}
            active={activeCategory === c.key}
            count={counts[c.key]}
            onClick={() => setActiveCategory(c.key)}
          >
            {t(`common:categories.${c.key}`)}
          </FilterChip>
        ))}
      </Flex>

      <CardBody p="0" display="block">
        {visible.length === 0 ? (
          <Box py="60px" textAlign="center">
            <Text fontSize="14px" color={mutedColor}>
              {t("common:allProducts.empty")}
            </Text>
          </Box>
        ) : (
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xl: "repeat(4, 1fr)",
            }}
            gap="18px"
          >
            {visible.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                image={
                  product.allProductsImageKey ? getImage(product.allProductsImageKey) : null
                }
              />
            ))}
          </Grid>
        )}
      </CardBody>
    </Card>
  );
};

export default AllProducts;
